import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Plus, Camera, X, AlertTriangle, Search, QrCode, History,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { getAforo, getInventarioPatio, buscarTicket, listValet, recibirValet, nuevaIdemKey, cambiarEstadoValet, guardarNotaValet, crearEvidencia, checkoutTicket, getCajaActiva, abrirCaja } from "@/services/operativaService";
import { getSucursal } from "@/services/sucursalesService";
import { aiParse as aiParseApi } from "@/services/aiService";
import { useAuth } from "@/context/AuthContext";
import { useAutoDismiss } from "@/hooks/useAutoDismiss";
import { ValetVehicleCard } from "@/components/ValetVehicleCard";
import { compressImageFile } from "@/lib/image";
import { sanitizeFolio } from "@/lib/utils";
import { playSiren, unlockAudio } from "@/lib/alertSound";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import ConfiguracionImpresoraModal from "@/components/ConfiguracionImpresoraModal";
import ConnectionStatus from "@/components/ConnectionStatus";
import { useTheme } from "@/context/ThemeContext";
import { enqueue, isNetworkError } from "@/lib/outbox";
import { loadPrinterConfig, buildValetReceptionTicket, buildValetPaymentReceipt, buildCortesiaTicket, printOrFallback, printNativePopup, openDrawer, imageToRasterCommand, logoWidthForPaper } from "@/lib/escposHelper";
import { Input } from "@/components/ui/input";
import { ESTADOS, LABEL, seqDeFolio, colDeEstado } from "@/domain/valet";
import ValetHeader from "@/components/valet/ValetHeader";
import AforoBar from "@/components/valet/AforoBar";
import QrCamScanner from "@/components/valet/QrCamScanner";
import ValetTickets from "@/components/valet/ValetTickets";
import PhotoDialog from "@/components/valet/PhotoDialog";
import AbrirTurnoModal from "@/components/valet/AbrirTurnoModal";
import LogoutBloqueadoModal from "@/components/valet/LogoutBloqueadoModal";
import RecibirVehiculoModal from "@/components/valet/RecibirVehiculoModal";
import RecepcionInventarioModal from "@/components/valet/RecepcionInventarioModal";
import CajaWarningModal from "@/components/valet/CajaWarningModal";
import RegresarModal from "@/components/valet/RegresarModal";
import ReciboSuccessModal from "@/components/valet/ReciboSuccessModal";
import PagoExitosoModal from "@/components/valet/PagoExitosoModal";
import CortesiaModal from "@/components/valet/CortesiaModal";
import { CortesiaThermalReceipt } from "@/components/terminal/CortesiaThermalReceipt";
import { CortesiaComprobantesDialog } from "@/components/terminal/CortesiaComprobantesDialog";
import NotaIncidenciaModal from "@/components/valet/NotaIncidenciaModal";
import GestionVehiculoModal from "@/components/valet/GestionVehiculoModal";
import PointModal from "@/components/valet/PointModal";
import HistorialModal from "@/components/valet/HistorialModal";
import { fmtDateTime, setActiveTimeZone } from "@/lib/datetime";

const emptyForm = { nombre_cliente: "", marca_modelo: "", color: "", placa: "", objetos_valor: "" };

export default function Valet() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sucursal, setSucursal] = useState(null);
  const [caja, setCaja] = useState(null);
  // Sprint Pernoctas — Recepción de Inventario Nocturno
  const [recepcionOpen, setRecepcionOpen] = useState(false);
  const [inventarioPatio, setInventarioPatio] = useState(null);
  const [tarifaValet, setTarifaValet] = useState(0);
  const [vozIaActiva, setVozIaActiva] = useState(false);
  const [fotoIaActiva, setFotoIaActiva] = useState(false);
  const [metodoRegistro, setMetodoRegistro] = useState("MANUAL");
  const [direccionCorta, setDireccionCorta] = useState("");
  const [logoBase64, setLogoBase64] = useState("");
  const [warnOpen, setWarnOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [escuchando, setEscuchando] = useState(false);
  const [interimTexto, setInterimTexto] = useState(""); // Sprint 34.1 — transcripción en vivo (Modo Flash)
  const dictarBufferRef = useRef("");
  // Dictado por voz: tolerancia a silencio (~2.8s) para permitir pausas naturales.
  const dictSilenceRef = useRef(null);
  const dictWantStopRef = useRef(false);
  const dictCommittedRef = useRef("");
  const dictSessionRef = useRef("");
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Anti-duplicación de recepción (Sprint 41.1). recibiendoRef: guard SÍNCRONO anti-burst + "en vuelo"
  // (se fija antes de cualquier await/estado). idemKeyRef: UNA clave por recepción (se reutiliza en
  // reintentos; solo se descarta al cerrar/éxito). inciertaRef: el último envío falló por RED (sin
  // respuesta) → resultado incierto; conservar la identidad hasta reconciliar con el backend.
  const recibiendoRef = useRef(false);
  const idemKeyRef = useRef(null);
  const inciertaRef = useRef(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [photoDefaultTipo, setPhotoDefaultTipo] = useState("Frente");
  const [regresarTarget, setRegresarTarget] = useState(null);
  const [regresando, setRegresando] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtroPago, setFiltroPago] = useState("todos");
  const [printerCfgOpen, setPrinterCfgOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const soundOnRef = useRef(true);
  const toggleSound = () => { unlockAudio(); setSoundOn((v) => { soundOnRef.current = !v; return !v; }); };
  // Menú móvil: acceso a las acciones de caja (expuestas por CajaTurnoBar vía ref).
  const cajaBarRef = useRef(null);
  const [cajaState, setCajaState] = useState({ hasCaja: false, canGasto: false, canReimprimir: true, valetSinCosto: false });
  // Sprint 34.0 — Modo Sol (tema claro de alto contraste), ahora GLOBAL vía ThemeContext.
  const { sunMode, toggleSunMode } = useTheme();
  // Aplica el Modo Sol también al <body> para que los modales portalizados adopten el estilo.
  useEffect(() => {
    document.body.classList.toggle("sun", sunMode);
    return () => document.body.classList.remove("sun");
  }, [sunMode]);
  // Aplica la clase en <body> para cubrir también los modales (portales de Radix).
  useEffect(() => {
    if (sunMode) document.body.classList.add("sun"); else document.body.classList.remove("sun");
    return () => document.body.classList.remove("sun");
  }, [sunMode]);
  const [recibo, setRecibo] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [aceptaTarjeta, setAceptaTarjeta] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [qrCamOpen, setQrCamOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const [metodoPago, setMetodoPago] = useState("Efectivo");
  const [cobrando, setCobrando] = useState(false);
  // Sprint 17 — Cobro con terminal física Mercado Pago Point (idéntico a Terminal.jsx)
  const [pointOpen, setPointOpen] = useState(false);
  const [pointDevice, setPointDevice] = useState("");
  const [pointDevices, setPointDevices] = useState([]);
  const [pointDevicesLoading, setPointDevicesLoading] = useState(false);
  const [pointStatus, setPointStatus] = useState("idle"); // idle|sending|waiting|timeout|error
  const [pointTxId, setPointTxId] = useState(null);
  const [pointTech, setPointTech] = useState(null);
  const [pointMsg, setPointMsg] = useState("");
  const [pointOrderStatus, setPointOrderStatus] = useState(null);
  const [comisionCfg, setComisionCfg] = useState(null);
  // Sprint 17.0 — Propinas (solo Tarjeta/Point)
  const [propinasActivas, setPropinasActivas] = useState(false);
  const [topePropinaPct, setTopePropinaPct] = useState(100); // Sprint 17.2 — tope configurable por sucursal
  const [propina, setPropina] = useState(0);
  const [propinaCustom, setPropinaCustom] = useState("");
  const pollRef = useRef(null);
  const pointTargetRef = useRef(null);
  const autoEntregarRef = useRef(false);
  // Sprint 15.3 — Boleto perdido + leyenda + trazabilidad
  const [bpMode, setBpMode] = useState(false);
  const [bpEvidencias, setBpEvidencias] = useState([]);
  const [bpUploading, setBpUploading] = useState(false);
  const [cobroInfo, setCobroInfo] = useState(null);
  // Sprint 25.0 — Comprobante de Pago de Valet (recibo de dinero tras cobro exitoso)
  const [pagoExitoso, setPagoExitoso] = useState(null);
  const pagoSnapshotRef = useRef(null);
  // Sprint 26.0 — Cortesías y notas de incidencia
  const [motivosCortesia, setMotivosCortesia] = useState([]);
  const [puedeCortesia, setPuedeCortesia] = useState(false);
  const [cortesiaOpen, setCortesiaOpen] = useState(false);
  const [cortesiaMotivo, setCortesiaMotivo] = useState("");
  // Sprint 43.0 — Comprobante de CORTESÍA (impresión + reimpresión del turno)
  const [cortesiaReceipt, setCortesiaReceipt] = useState(null);
  const [cortesiasTurno, setCortesiasTurno] = useState([]);
  const [cortesiasLoading, setCortesiasLoading] = useState(false);
  const [cortesiaComprobOpen, setCortesiaComprobOpen] = useState(false);
  const [notaTarget, setNotaTarget] = useState(null);
  const [notaTexto, setNotaTexto] = useState("");
  const [notaSaving, setNotaSaving] = useState(false);
  const [autoEntregar, setAutoEntregar] = useState(false);
  const [leyendaTicket, setLeyendaTicket] = useState("");
  const [horarioAtencion, setHorarioAtencion] = useState(""); // Punto 2 — horario formateado para el boleto
  const [costoBoletoPerdido, setCostoBoletoPerdido] = useState(0);
  const itemsRef = useRef([]);
  itemsRef.current = items;
  const [historyOpen, setHistoryOpen] = useState(false);
  // Sprint 29.0 — modelo de cobro de la sucursal, aforo y candado de salida
  const [modeloCobro, setModeloCobro] = useState("COMBINADO");
  const [cortesiasActivas, setCortesiasActivas] = useState(false); // Sprint 31.1 — gating estricto: oculto hasta confirmar config de sucursal
  const [aforo, setAforo] = useState(null);
  const [logoutWarnOpen, setLogoutWarnOpen] = useState(false);
  // Sprint 31.0 — modal directo de Abrir Turno (fondo inicial) disponible en todos los modelos
  const [abrirTurnoOpen, setAbrirTurnoOpen] = useState(false);
  const [fondoTurno, setFondoTurno] = useState("");
  const [openingTurno, setOpeningTurno] = useState(false);
  const [wsStatus, setWsStatus] = useState("connecting");
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const ownFoliosRef = useRef(new Set());

  // Sprint 24.0 — Semáforo de calidad de servicio: ticker de tiempo real para el cronómetro
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Sprint 34.0 — Desbloqueo del audio en el primer gesto del usuario (para que la sirena suene por WS)
  useEffect(() => {
    const unlock = () => { unlockAudio(); window.removeEventListener("pointerdown", unlock); };
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);
  // SLA de la columna "Solicitado": cuenta desde la hora exacta de solicitud del cliente.
  const slaFor = (v) => {
    const startMs = v.solicitado_at ? new Date(v.solicitado_at).getTime() : nowTick;
    const secs = Math.max(0, Math.floor((nowTick - startMs) / 1000));
    const mins = Math.floor(secs / 60);
    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    const tier = mins > 15 ? "red" : mins >= 11 ? "orange" : "green";
    return { mins, secs, label: `${mm}:${ss}`, tier };
  };
  // Sprint 24.1 — formato del tiempo final congelado (sello de servicio).
  const fmtEsperaFinal = (seg) => {
    if (seg == null) return null;
    const m = Math.floor(seg / 60);
    const s = seg % 60;
    if (m <= 0) return `${s} s`;
    return s > 0 ? `${m} min ${s} s` : `${m} min`;
  };
  // Sprint 34.0 — Contador de permanencia en "Por Entregar": inicia al marcar "Listo" (pagado_at).
  // Si supera 15 min (>900s) marca "over" para parpadeo rojo intenso y contador resaltado.
  const permanenceFor = (v) => {
    if (!v.pagado_at) return null;
    const startMs = new Date(v.pagado_at).getTime();
    if (Number.isNaN(startMs)) return null;
    const secs = Math.max(0, Math.floor((nowTick - startMs) / 1000));
    const mm = String(Math.floor(secs / 60)).padStart(2, "0");
    const ss = String(secs % 60).padStart(2, "0");
    return { secs, label: `${mm}:${ss}`, over: secs > 900 };
  };

  const refreshAforo = useCallback(async (sidArg) => {
    if (!sidArg) return;
    try { const { data } = await getAforo(sidArg); setAforo(data); } catch { /* noop */ }
  }, []);

  const load = useCallback(async (sid) => {
    try {
      const { data } = await listValet(sid);
      setItems(data);
      refreshAforo(sid);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [refreshAforo]);

  // Sprint 10.0 — Sincronización en tiempo real (WebSocket) del Kanban, aislado por sucursal
  useEffect(() => {
    if (!sucursal?.id) return;
    let closedByUs = false;
    const connect = () => {
      setWsStatus("connecting");
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/ws/valet/${sucursal.id}`);
      wsRef.current = ws;
      ws.onopen = () => setWsStatus("connected");
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          const { type, ticket } = msg;
          // Sprint 15.2 — Cliente solicitó su auto desde la landing pública
          if (type === "VALET_SOLICITADO") {
            if (soundOnRef.current) playSiren(); // Sprint 34.0 — alerta sonora de alto impacto
            toast(`🔔 Cliente solicitó su auto: ${msg.folio || ticket?.folio_impreso || ""}`, { description: "Preparar entrega en bahía" });
            load(sucursal.id);
            return;
          }
          // Sprint 23.1 — El cliente firmó conformidad desde su celular -> mover a 'Entregado' en vivo
          if (type === "VALET_ENTREGADO_CLIENTE") {
            if (soundOnRef.current) chime();
            toast.success(`✅ Cliente firmó conformidad: ${msg.folio || ""}`, { description: msg.movido_entregado ? "Vehículo movido a Entregado" : "Conformidad registrada" });
            load(sucursal.id);
            return;
          }
          // Alertas operativas de Valet (cruce de umbral SLA: Solicitado / bahía) — visual + sirena
          if (type === "VALET_ALERTA") {
            if (soundOnRef.current) playSiren();
            const titulo = msg.titulo || "Alerta de Valet";
            toast.error(`🚨 ${titulo}`, { description: msg.mensaje || (msg.folio ? `Folio ${msg.folio}` : "") });
            return;
          }
          if (!ticket?.id_detalle) return;
          setItems((prev) => {
            const idx = prev.findIndex((i) => i.id_detalle === ticket.id_detalle);
            if (idx !== -1) { const c = [...prev]; c[idx] = ticket; return c; }
            // TICKET_CREADO de otro dispositivo -> aviso sonoro + toast
            if (type === "TICKET_CREADO" && !ownFoliosRef.current.has(ticket.folio_impreso)) {
              if (soundOnRef.current) beep();
              toast(`Nuevo vehículo: ${ticket.folio_impreso}`, { description: [ticket.placa, ticket.marca_modelo].filter(Boolean).join(" · ") || "Recepción en otra terminal" });
            }
            ownFoliosRef.current.delete(ticket.folio_impreso);
            return [ticket, ...prev];
          });
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setWsStatus("disconnected");
        if (!closedByUs) reconnectRef.current = setTimeout(connect, 2000); // auto-reconnect
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => {
      closedByUs = true;
      clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [sucursal?.id, load]);

  // Beep de confirmación (Web Audio) al escanear un folio válido
  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.value = 880;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.07, ctx.currentTime);
      o.start();
      o.stop(ctx.currentTime + 0.12);
      o.onended = () => ctx.close();
    } catch { /* audio no disponible */ }
  };

  // Chime de notificación (Web Audio) — llamada de cliente entrante (SOLICITADO). Dos tonos ascendentes, más notorio que el beep.
  const chime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const tono = (freq, start, dur) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur);
      };
      tono(784, 0, 0.22);      // Sol5
      tono(1046, 0.18, 0.35);  // Do6
      setTimeout(() => { try { ctx.close(); } catch {} }, 800);
    } catch { /* audio no disponible */ }
  };

  useEffect(() => {
    const raw = localStorage.getItem("pap_sucursal");
    if (!raw) { navigate("/operativo/seleccion", { replace: true }); return; }
    const s = JSON.parse(raw);
    setSucursal(s);
    if (s.zona_horaria) setActiveTimeZone(s.zona_horaria);
    (async () => {
      try {
        const { data } = await getCajaActiva(s.id);
        setCaja(data);
        // Sprint Pernoctas — recepción de inventario nocturno (una vez por turno en este navegador)
        if (data?.id_caja) {
          const seenKey = `pap_recepcion_seen_${data.id_caja}`;
          if (!sessionStorage.getItem(seenKey)) {
            try {
              const { data: inv } = await getInventarioPatio(s.id);
              if (inv?.conteo > 0) { setInventarioPatio(inv); setRecepcionOpen(true); }
            } catch { /* noop */ }
            sessionStorage.setItem(seenKey, "1");
          }
        }
        try {
          const { data: suc } = await getSucursal(s.id);
          if (suc?.zona_horaria) setActiveTimeZone(suc.zona_horaria);
          setModeloCobro(suc?.modelo_cobro || "COMBINADO");
          setCortesiasActivas(suc?.cortesias_activas !== false);
          setTarifaValet(suc?.valet_activo ? Number(suc.tarifa_valet || 0) : 0);
          setAceptaTarjeta(!!suc?.cobro_tarjeta_mp);
          setDireccionCorta(suc?.calle_numero || [suc?.calle, suc?.numero].filter(Boolean).join(" ") || "");
          setLogoBase64(suc?.logo_base64 || "");
          setLeyendaTicket(suc?.leyenda_ticket_corta || "");
          setHorarioAtencion(suc?.horario_atencion || suc?.horario || "");
          setCostoBoletoPerdido(Number(suc?.costo_boleto_perdido || 0));
          setPropinasActivas(!!suc?.propinas_activas);
          setTopePropinaPct(Number(suc?.tope_propina_pct ?? 100));
          setVozIaActiva(!!suc?.voz_ia_activa);
          setFotoIaActiva(!!suc?.foto_ia_activa);
        } catch { setTarifaValet(0); }
        await load(s.id);
      } finally { setLoading(false); }
    })();
  }, [navigate, load]);

  useEffect(() => {
    api.get("/mi-empresa").then((r) => setComisionCfg(r.data)).catch(() => {});
    api.get("/operativa/motivos-cortesia").then((r) => setMotivosCortesia(r.data?.motivos || [])).catch(() => {});
  }, []);

  // Sprint 26.0 — permiso de cortesía (según sucursal seleccionada)
  useEffect(() => {
    if (!sucursal?.id) return;
    api.get(`/operativa/mis-permisos?id_sucursal=${sucursal.id}`)
      .then((r) => setPuedeCortesia(!!r.data?.puede_autorizar_cortesia)).catch(() => {});
  }, [sucursal]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Sprint 29.0 — candado de salida de turno: avisa al cerrar la pestaña/recargar si hay turno con movimientos
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!(caja && items.length > 0)) return;
      e.preventDefault();
      e.returnValue = "Tienes un turno de caja activo. Realiza el corte antes de salir.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [caja, items.length]);

  const abrirRecepcion = () => {
    // Sprint 8.0 — candado preventivo: Valet con costo exige caja abierta
    if (!caja && tarifaValet > 0) { setWarnOpen(true); return; }
    // Recepción NUEVA: limpia datos y clave. Excepción: si quedó una recepción con resultado
    // INCIERTO (falló por red, no sabemos si el backend creó el ticket), conservamos form + clave
    // para reintentar con la MISMA identidad (no duplica).
    if (!inciertaRef.current) {
      idemKeyRef.current = null;
      setForm(emptyForm);
    }
    setFormOpen(true);
  };

  // Cierre del modal de recepción (X). Comportamiento según estado:
  //  - EN VUELO (recibiendoRef): no cerrar; evita que cerrar/reabrir cree una operación nueva.
  //  - INCIERTO (inciertaRef): cierra la UI pero CONSERVA form + clave para reintentar con la misma.
  //  - No enviado / error con respuesta / éxito ya resuelto: descarta la recepción y su clave.
  const cerrarRecepcion = () => {
    if (recibiendoRef.current) {
      toast.message("Procesando recepción…", { description: "Espera un momento a que termine." });
      return;
    }
    try { recognitionRef.current?.stop(); } catch {}
    setEscuchando(false);
    setAiLoading(false);
    if (inciertaRef.current) { setFormOpen(false); return; }
    idemKeyRef.current = null;
    setForm(emptyForm);
    setFormOpen(false);
  };

  const irAbrirCaja = () => {
    // Sprint 31.0 — flujo directo: el botón amarillo abre de inmediato la captura de Fondo Inicial (sin redirecciones ni alertas intermedias)
    setWarnOpen(false);
    setFondoTurno("");
    setAbrirTurnoOpen(true);
  };

  const abrirTurnoValet = async () => {
    const val = parseFloat(fondoTurno);
    if (isNaN(val) || val < 0) return toast.error("Ingresa un fondo inicial válido");
    setOpeningTurno(true);
    try {
      await abrirCaja({ id_sucursal: sucursal.id, fondo_inicial: val });
      const { data } = await getCajaActiva(sucursal.id);
      setCaja(data);
      // Al abrir un nuevo turno, recargar el tablero para que los ENTREGADOS de
      // turnos anteriores dejen de aparecer (se conservan en historial/reportes).
      await load(sucursal.id);
      toast.success("Turno abierto");
      setAbrirTurnoOpen(false); setFondoTurno("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setOpeningTurno(false); }
  };

  const recibir = async () => {
    if (recibiendoRef.current) return; // (1) guard SÍNCRONO anti-burst (antes de cualquier await/estado)
    recibiendoRef.current = true;
    setSaving(true);
    if (!idemKeyRef.current) idemKeyRef.current = nuevaIdemKey(); // (2) UNA clave por recepción
    const key = idemKeyRef.current;
    try {
      const { data } = await recibirValet(
        { id_sucursal: sucursal.id, id_caja: caja?.id_caja || null, metodo_registro: metodoRegistro, ...form },
        { idemKey: key }, // reutiliza la MISMA clave en todos los reintentos
      );
      idemKeyRef.current = null; // éxito → recepción cerrada; la próxima será una recepción NUEVA
      inciertaRef.current = false;
      ownFoliosRef.current.add(data.folio_impreso); // evita autonotificarse por el evento WS propio
      setForm(emptyForm);
      setMetodoRegistro("MANUAL");
      setFormOpen(false);
      setRecibo(data);
      setSuccessOpen(true);
      load(sucursal.id);
      // Sprint 14.1 — impresión automática del boleto de recepción (respeta la config del dispositivo)
      imprimirTicketValet(data);
    } catch (err) {
      // Con respuesta (4xx/5xx): el backend respondió → NO hay ticket nuevo; se conserva la clave.
      // Sin respuesta (timeout/red, reintentos agotados): resultado INCIERTO → preservamos la clave
      // para reintentar con la MISMA (si el backend sí creó el ticket, el retry devuelve el existente).
      inciertaRef.current = !err?.response;
      toast.error(formatApiError(err.response?.data?.detail));
    }
    finally { recibiendoRef.current = false; setSaving(false); }
  };

  const avanzar = async (v) => {
    const idx = ESTADOS.indexOf(v.estado_valet);
    if (idx >= ESTADOS.length - 1) return;
    const next = ESTADOS[idx + 1];
    // Candado financiero: no se puede ENTREGAR sin haber cobrado (cuando la sucursal cobra valet)
    if (next === "Entregado" && cobraValet && !esPagado(v)) {
      toast.error("⚠️ Este vehículo debe ser cobrado antes de registrar su entrega.");
      openCobro(v, false, true);
      return;
    }
    try {
      await cambiarEstadoValet(v.id_detalle, { estado_valet: next });
      load(sucursal.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Sprint 9.0 (Fase 4) — avanza estado a estado hasta llegar al destino (respeta el candado ±1 del backend)
  const avanzarHasta = async (v, target) => {
    let idx = ESTADOS.indexOf(v.estado_valet);
    const t = ESTADOS.indexOf(target);
    while (idx < t) {
      await cambiarEstadoValet(v.id_detalle, { estado_valet: ESTADOS[idx + 1] });
      idx++;
    }
  };

  // Escáner QR/código de barras: busca por folio y abre el modal de gestión.
  // Acepta tanto el folio en texto plano como la URL completa de la Landing (…/t/FOLIO).
  const onScan = (folio) => {
    const raw = (folio || "").trim();
    const m = raw.match(/\/t\/([^/?#]+)/i);
    const extracted = m ? decodeURIComponent(m[1]) : raw;
    const f = sanitizeFolio(extracted); // normaliza comillas/acentos del lector USB a guiones
    if (!f) return;
    const v = itemsRef.current.find((i) => (i.folio_impreso || "").toUpperCase() === f);
    setScanValue("");
    if (!v) return toast.error(`No se encontró un vehículo con el folio ${f}`);
    beep();
    setMetodoPago("Efectivo");
    setScanTarget(v);
  };

  // Escáner USB unificado (hook compartido con Caseta): comportamiento idéntico.
  useBarcodeScanner({ onScan });

  const cobrarYSolicitar = async () => {
    const v = scanTarget;
    if (!v) return;
    const needsCobro = tarifaValet > 0 && v.estado_ticket === "Activo";
    if (bpMode && bpEvidencias.length === 0) {
      toast.error("Captura al menos una foto de acreditación de propiedad (INE/placa) para el boleto perdido.");
      return;
    }
    // Sprint 17 — Tarjeta física por Mercado Pago Point (mismo flujo que Terminal).
    // El boleto perdido va por checkout manual (el backend Point no aplica penalización).
    if (metodoPago === "Tarjeta" && !bpMode && needsCobro) {
      abrirPagoTarjeta();
      return;
    }
    setCobrando(true);
    try {
      if (needsCobro || bpMode) {
        const estOff = Number(cobroInfo?.monto_estacionamiento || 0);
        const valOff = cobroInfo ? Number(cobroInfo.monto_valet || 0) : (tarifaValet || 0);
        try {
          const { data: chk } = await checkoutTicket({ id_ticket: v.id_ticket, metodo_pago: metodoPago, id_caja: caja?.id_caja || null, es_boleto_perdido: bpMode });
          // Sprint 25.0 — snapshot del pago para el Comprobante de Pago (efectivo: sin propina ni comisión)
          pagoSnapshotRef.current = {
            folio: v.folio_impreso, placa: v.placa || "",
            fecha: fmtDateTime(new Date()), metodo: metodoPago,
            estancia: Number(chk.monto_estacionamiento || 0), valet: Number(chk.monto_valet || 0),
            penalizacion: Number(chk.monto_penalizacion_boleto || 0), propina: 0, comision: 0,
            total: Number(chk.gran_total || 0), cobradoPor: user?.usuario_corto || user?.email || "",
          };
        } catch (err) {
          // Sprint 35.0 — Sin red: cobro en efectivo OFFLINE del valet (respeta folio/monto/hora local).
          if (isNetworkError(err) && metodoPago !== "Tarjeta" && !bpMode) {
            const total = Number((estOff + valOff).toFixed(2));
            await enqueue({
              url: "/operativa/checkout",
              body: { id_ticket: v.id_ticket, metodo_pago: metodoPago, id_caja: caja?.id_caja || null, offline: true, offline_salida: new Date().toISOString(), offline_monto_estacionamiento: estOff, offline_monto_valet: valOff, offline_total: total },
              kind: "checkout", label: `Cobro valet offline ${v.folio_impreso} · $${total.toFixed(2)}`, folio_local: v.folio_impreso,
            });
            pagoSnapshotRef.current = {
              folio: v.folio_impreso, placa: v.placa || "", fecha: fmtDateTime(new Date()), metodo: metodoPago,
              estancia: estOff, valet: valOff, penalizacion: 0, propina: 0, comision: 0, total, cobradoPor: user?.usuario_corto || user?.email || "",
            };
            toast.warning(`Cobro OFFLINE $${total.toFixed(2)} · folio ${v.folio_impreso}. Se sincronizará al reconectar.`);
            try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) await openDrawer(cfg); } catch {}
            setScanTarget(null); setBpMode(false); setBpEvidencias([]); setAutoEntregar(false);
            setPagoExitoso(pagoSnapshotRef.current);
            return;
          }
          throw err;
        }
        // Sprint 14.1 — apertura de cajón en todo pago exitoso (si la preferencia está activa)
        try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) await openDrawer(cfg); } catch {}
      }
      // Cuando ya está en/adelante de "Solicitado" (p.ej. columna Por Entregar), avanzarHasta es no-op y solo cobra.
      await avanzarHasta(v, "Solicitado");
      if (autoEntregar) {
        // Sprint 16-mejora: candado de entrega -> tras cobrar, entrega en un solo paso
        await avanzarHasta(v, "Entregado");
        toast.success(`Cobrado (${metodoPago}) · Vehículo entregado`);
      } else {
        toast.success((needsCobro || bpMode) ? `Cobrado (${metodoPago}) · Auto solicitado` : "Auto solicitado");
      }
      setScanTarget(null); setBpMode(false); setBpEvidencias([]); setAutoEntregar(false);
      // Sprint 25.0 — tras un cobro exitoso, ofrecer el Comprobante de Pago (no cerrar en seco)
      if (needsCobro || bpMode) setPagoExitoso(pagoSnapshotRef.current);
      load(sucursal.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCobrando(false); }
  };

  // ---------- Cobro con terminal física Mercado Pago Point (Orders API) — idéntico a Terminal.jsx ----------
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const cerrarPagoPoint = () => {
    stopPoll();
    setPointOpen(false);
    if (pointStatus !== "waiting" && pointStatus !== "timeout") {
      setPointStatus("idle"); setPointTxId(null); setPointTech(null); setPointOrderStatus(null); setPointMsg("");
    }
  };

  const abrirPagoTarjeta = () => {
    pointTargetRef.current = scanTarget;
    autoEntregarRef.current = autoEntregar;
    setPropina(0); setPropinaCustom("");
    stopPoll(); setPointStatus("idle"); setPointTxId(null); setPointTech(null); setPointMsg(""); setPointOrderStatus(null);
    setPointOpen(true); cargarDevices();
  };

  const cargarDevices = async () => {
    const s = sucursal?.id;
    setPointDevicesLoading(true);
    try {
      const { data } = await api.get(`/mercadopago/point/devices?id_sucursal=${s}`);
      const list = data.devices || [];
      setPointDevices(list);
      const saved = sessionStorage.getItem(`pap_point_device_${s}`) || "";
      if (saved && !list.some((d) => d.id === saved)) setPointDevice("");
      else if (saved) setPointDevice(saved);
      if (!saved && list.length === 1) setPointDevice(list[0].id);
    } catch (err) {
      setPointDevices([]);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPointDevicesLoading(false); }
  };

  const elegirDevice = (id) => {
    setPointDevice(id);
    sessionStorage.setItem(`pap_point_device_${sucursal?.id}`, id);
  };

  const aplicarEstado = (data) => {
    setPointTech({
      endpoint: data.endpoint, http_status: data.http_status, order_id: data.order_id,
      order_status: data.order_status, status_detail: data.status_detail,
      terminal_id: data.terminal_id, date_created: data.date_created,
    });
    setPointOrderStatus(data.order_status || null);
    setPointMsg(data.mensaje || "");

    if (data.estado === "approved") {
      stopPoll();
      setPointStatus("idle");
      setPointOpen(false);
      try { const cfg = loadPrinterConfig(); if (cfg.abrirCajon) openDrawer(cfg).catch(() => {}); } catch {}
      (async () => {
        const v = pointTargetRef.current;
        try {
          if (v) {
            await avanzarHasta(v, "Solicitado");
            if (autoEntregarRef.current) {
              await avanzarHasta(v, "Entregado");
              toast.success("Cobrado (Tarjeta) · Vehículo entregado");
            } else {
              toast.success("Cobrado (Tarjeta) · Auto solicitado");
            }
          }
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        setScanTarget(null); setBpMode(false); setBpEvidencias([]); setAutoEntregar(false);
        // Sprint 25.0 — cobro con tarjeta aprobado: ofrecer el Comprobante de Pago
        if (pagoSnapshotRef.current) setPagoExitoso(pagoSnapshotRef.current);
        if (sucursal?.id) load(sucursal.id);
      })();
    } else if (data.estado === "expired") {
      stopPoll(); setPointStatus("error"); setPointMsg(data.mensaje || "La orden expiró. Genera un nuevo cobro.");
    } else if (["rejected", "canceled"].includes(data.estado)) {
      stopPoll(); setPointStatus("error");
    }
    // created | at_terminal | action_required | pending -> seguimos esperando
  };

  const consultarEstado = async (txId) => {
    try {
      const { data } = await api.get(`/mercadopago/point/estado?id_transaccion=${txId}`);
      aplicarEstado(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const iniciarPolling = (txId) => {
    stopPoll();
    const inicio = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - inicio > 300000) {
        stopPoll();
        const d = await consultarEstado(txId);
        const est = d?.estado;
        if (["approved", "rejected", "canceled", "expired"].includes(est)) return;
        setPointStatus("timeout");
        return;
      }
      await consultarEstado(txId);
    }, 2000);
  };

  const enviarCobroPoint = async () => {
    const device = pointDevice.trim();
    const s = sucursal?.id;
    const v = pointTargetRef.current || scanTarget;
    if (!device) return toast.error("Selecciona la terminal");
    if (!v) return;
    sessionStorage.setItem(`pap_point_device_${s}`, device);
    const est = Number(cobroInfo?.monto_estacionamiento || 0);
    const val = cobroInfo ? Number(cobroInfo.monto_valet || 0) : (tarifaValet || 0);
    const monto = Math.round((est + val) * 100) / 100;
    const prop = propinasActivas ? Math.max(0, Number(propina) || 0) : 0;
    const topeMax = Math.round(monto * (Number(topePropinaPct) || 100) / 100 * 100) / 100;
    if (prop > topeMax + 0.0001) return toast.error(`La propina no puede exceder el ${Number(topePropinaPct) || 100}% del subtotal (máx. $${topeMax.toFixed(2)}).`);
    setPointStatus("sending"); setPointMsg(""); setPointOrderStatus(null);
    // Sprint 25.0 — snapshot del pago con tarjeta para el Comprobante de Pago (subtotal + propina + comisión)
    const trasladaCom = !!comisionCfg?.trasladar_comision_tarjeta;
    const baseCom = Math.round((monto + prop) * 100) / 100;
    const comisionCalc = trasladaCom
      ? Math.round((baseCom * (Number(comisionCfg.tasa_comision_porcentaje) / 100) + Number(comisionCfg.comision_cargo_fijo)) * 100) / 100
      : 0;
    pagoSnapshotRef.current = {
      folio: v.folio_impreso, placa: v.placa || "",
      fecha: fmtDateTime(new Date()), metodo: "Tarjeta",
      estancia: est, valet: val, penalizacion: 0, propina: prop, comision: comisionCalc,
      total: Math.round((monto + prop + comisionCalc) * 100) / 100,
      cobradoPor: user?.usuario_corto || user?.email || "",
    };
    try {
      const { data } = await api.post("/mercadopago/point/cobrar", {
        id_sucursal: s, id_ticket: v.id_ticket, id_caja: caja?.id_caja || null, monto, propina: prop, id_device: device,
      }, { timeout: 60000 });
      setPointTxId(data.id_transaccion);
      setPointTech({ endpoint: "POST /v1/orders", http_status: data.http_status, order_id: data.order_id,
        order_status: data.order_status, status_detail: null, terminal_id: data.terminal_id, date_created: null });
      setPointOrderStatus(data.order_status || "created");
      if (data.duplicada) toast.info(data.mensaje || "Ya existe una orden pendiente para este folio.");
      setPointStatus("waiting");
      iniciarPolling(data.id_transaccion);
    } catch (err) {
      setPointStatus("error");
      setPointMsg(formatApiError(err.response?.data?.detail));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const consultarManual = async () => {
    if (!pointTxId) return;
    const data = await consultarEstado(pointTxId);
    if (data && ["created", "at_terminal", "action_required"].includes(data.estado)) {
      setPointStatus("waiting"); iniciarPolling(pointTxId);
    }
  };

  const cancelarPendiente = async () => {
    if (!pointTxId) return;
    try {
      const { data } = await api.post(`/mercadopago/point/cancelar?id_transaccion=${pointTxId}`);
      toast[data.ok ? "success" : "warning"](data.mensaje || "");
      stopPoll();
      if (data.ok && data.estado === "canceled") {
        setPointStatus("error"); setPointMsg(data.mensaje || "Cobro cancelado y confirmado por Mercado Pago.");
      } else if (data.estado === "expired") {
        setPointStatus("error"); setPointMsg(data.mensaje || "La orden expiró; se registró como Expirada.");
      } else if (data.estado === "processed") {
        consultarEstado(pointTxId);
      } else if (data.estado === "at_terminal") {
        setPointStatus("waiting"); setPointMsg(data.mensaje || ""); iniciarPolling(pointTxId);
      } else {
        setPointStatus("waiting"); setPointMsg(data.mensaje || ""); iniciarPolling(pointTxId);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Sprint 15.3 — abrir el modal de cobro desde una tarjeta (bp=true pre-activa Boleto Perdido)
  const soloAcreditacion = (arr) => (arr || []).filter((f) => f.tipo_evidencia === "Acreditacion");
  const openCobro = async (v, bp, auto = false) => {
    setMetodoPago("Efectivo");
    setBpMode(bp);
    setBpEvidencias([]);
    setCobroInfo(null);
    setAutoEntregar(auto);
    setScanTarget(v);
    if (bp) { try { const { data } = await api.get(`/operativa/evidencias?id_ticket=${v.id_ticket}`); setBpEvidencias(soloAcreditacion(data)); } catch {} }
  };

  const capturarEvidenciaBP = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !scanTarget) return;
    setBpUploading(true);
    try {
      const dataUrl = await compressImageFile(file, { maxSize: 1000, quality: 0.7 });
      await crearEvidencia({ id_ticket: scanTarget.id_ticket, tipo_evidencia: "Acreditacion", url_imagen: dataUrl });
      const { data } = await api.get(`/operativa/evidencias?id_ticket=${scanTarget.id_ticket}`);
      setBpEvidencias(soloAcreditacion(data));
      toast.success("Evidencia de acreditación guardada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar la evidencia"); }
    finally { setBpUploading(false); }
  };

  const infoCobrado = (v) => {
    const fecha = v.fecha_cobro ? fmtDateTime(v.fecha_cobro) : "—";
    toast.success(`Vehículo cobrado previamente por: ${v.cobrado_por_nombre || "—"} el ${fecha}`, { duration: 6000 });
  };

  // Sprint 15.3 — al abrir el modal de gestión (por escaneo o tarjeta), carga el desglose autoritativo (estancia + valet + penalización)
  useEffect(() => {
    if (scanTarget && sucursal?.id) {
      buscarTicket(sucursal.id, scanTarget.folio_impreso)
        .then(({ data }) => setCobroInfo(data)).catch(() => {});
    }
  }, [scanTarget, sucursal]);

  // Comprobante de valet: impresión doble (Cliente + Control) vía motor ESC/POS
  const imprimirTicketValet = async (v) => {
    const cfg = loadPrinterConfig();
    const doNative = () => { setRecibo(v); setTimeout(() => window.print(), 150); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const logoCmd = logoBase64 ? await imageToRasterCommand(logoBase64, logoWidthForPaper(cfg)) : null;
      const bytes = buildValetReceptionTicket({
        brand: sucursal?.nombre || "PARKNOVA",
        direccionCorta,
        horario: horarioAtencion,
        logo: logoCmd,
        folio: v.folio_impreso,
        fecha: fmtDateTime(v.fecha_entrada || new Date()),
        placa: v.placa || "",
        marca: v.marca_modelo || "",
        color: v.color || "",
        objetos: v.objetos_valor || "",
        nombre: v.nombre_cliente || "",
        valetOperador: user?.usuario_corto || user?.email || "—",
        qrData: `${window.location.origin}/t/${v.folio_impreso}`,
        leyenda: leyendaTicket,
        tarifaTexto: Number(tarifaValet) > 0 ? `$${Number(tarifaValet).toFixed(2)} Tarifa Fija` : null,
        cfg,
      });
      const r = await printOrFallback(bytes, cfg);
      if (r === "nativo") { doNative(); return; } // fallback silencioso (USB no disponible)
      toast.success("Boleto de valet enviado (Cliente + Control)");
    } catch (e) { toast.error(e?.message || "No se pudo imprimir"); }
  };

  // Reimpresión térmica desde una tarjeta (sin cambiar estado)
  const reimprimir = (v) => imprimirTicketValet(v);

  // Sprint 26.0 — Guardar nota de incidencia (teclado) asociada al ticket del valet
  const guardarNota = async () => {
    if (!notaTarget) return;
    setNotaSaving(true);
    try {
      await guardarNotaValet(notaTarget.id_detalle, { nota: notaTexto });
      toast.success("Nota de incidencia guardada");
      setNotaTarget(null); setNotaTexto("");
      if (sucursal?.id) load(sucursal.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setNotaSaving(false); }
  };

  // Sprint 26.0 — Aplicar cortesía (cobro $0 con motivo). Requiere permiso especial.
  const aplicarCortesia = async () => {
    const v = scanTarget;
    if (!v) return;
    if (!cortesiaMotivo) { toast.error("Selecciona un motivo de cortesía"); return; }
    setCobrando(true);
    try {
      const { data } = await checkoutTicket({ id_ticket: v.id_ticket, metodo_pago: "Efectivo", id_caja: caja?.id_caja || null, es_cortesia: true, motivo_cortesia: cortesiaMotivo });
      await avanzarHasta(v, "Solicitado");
      toast.success(`Cortesía aplicada · ${cortesiaMotivo}`);
      setCortesiaOpen(false); setCortesiaMotivo(""); setScanTarget(null);
      // Auto-impresión best-effort (la cortesía ya quedó registrada; si falla, reimprimible desde el menú).
      try { imprimirCortesia({ folio: data?.folio || v.folio_impreso, motivo: cortesiaMotivo, importe: data?.monto_cortesia_otorgado, operador: user?.usuario_corto || user?.email, fecha: new Date() }); } catch { /* noop */ }
      if (sucursal?.id) load(sucursal.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCobrando(false); }
  };

  // Sprint 25.0 — Comprobante de Pago (recibo de dinero) del Valet: impresión y reimpresión.
  const imprimirComprobantePago = async (pago) => {
    if (!pago) return;
    const cfg = loadPrinterConfig();
    try {
      const bytes = buildValetPaymentReceipt({
        brand: sucursal?.nombre || "PARKNOVA",
        folio: pago.folio,
        placa: pago.placa || "",
        fecha: pago.fecha || fmtDateTime(new Date()),
        metodoPago: pago.metodo || "",
        estancia: pago.estancia || 0,
        valet: pago.valet || 0,
        penalizacion: pago.penalizacion || 0,
        propina: pago.propina || 0,
        comision: pago.comision || 0,
        total: pago.total || 0,
        cobradoPor: pago.cobradoPor || user?.usuario_corto || user?.email || "—",
        cfg,
      });
      const r = await printOrFallback(bytes, cfg);
      if (r === "nativo") {
        const money = (x) => `$${Number(x || 0).toFixed(2)}`;
        const sub = Math.round(((pago.estancia || 0) + (pago.valet || 0) + (pago.penalizacion || 0)) * 100) / 100;
        const rowH = (l, v) => `<div style="display:flex;justify-content:space-between"><span>${l}</span><span>${money(v)}</span></div>`;
        const body = `
          <div class="brand">COMPROBANTE DE PAGO</div>
          <div class="ok">- VALET -</div>
          <div class="sm">${sucursal?.nombre || "PARKNOVA"}</div>
          <div class="sep"></div>
          <div class="sm" style="text-align:left">
            <div>Folio: ${pago.folio || ""}</div>
            ${pago.placa ? `<div>Placas: ${pago.placa}</div>` : ""}
            <div>Fecha/Hora: ${pago.fecha || fmtDateTime(new Date())}</div>
            ${pago.metodo ? `<div>Metodo pago: ${pago.metodo}</div>` : ""}
          </div>
          <div class="sep"></div>
          <div class="sm" style="text-align:left">
            ${pago.estancia > 0 ? rowH("Estancia", pago.estancia) : ""}
            ${pago.valet > 0 ? rowH("Servicio Valet", pago.valet) : ""}
            ${pago.penalizacion > 0 ? rowH("Penalizacion", pago.penalizacion) : ""}
            ${rowH("Subtotal", sub)}
            ${pago.propina > 0 ? rowH("Propina", pago.propina) : ""}
            ${pago.comision > 0 ? rowH("Cargo por servicio", pago.comision) : ""}
          </div>
          <div class="sep"></div>
          <div class="ok">TOTAL PAGADO ${money(pago.total)}</div>
          <div class="sep"></div>
          <div class="sm">Gracias por su preferencia</div>`;
        printNativePopup({ papel: cfg.papel || "80", title: "Comprobante de Pago", body });
        return;
      }
      toast.success("Comprobante de pago enviado a la impresora");
    } catch (e) { toast.error(e?.message || "No se pudo imprimir el comprobante"); }
  };

  // Deriva el desglose de pago de una tarjeta ya cobrada (para reimpresión desde 'Entregado').
  const pagoDesdeCard = (v) => {
    const est = Number(v.monto_base_pagado || 0);
    const val = Number(v.monto_valet_pagado || 0);
    const pen = Number(v.monto_penalizacion_boleto || 0);
    const prop = Number(v.monto_propina || 0);
    const total = Number(v.total_pagado || 0);
    const comision = Math.max(0, Math.round((total - est - val - pen - prop) * 100) / 100);
    return {
      folio: v.folio_impreso, placa: v.placa || "",
      fecha: v.fecha_cobro ? fmtDateTime(v.fecha_cobro) : fmtDateTime(new Date()),
      metodo: v.metodo_pago_cobro || "", estancia: est, valet: val, penalizacion: pen,
      propina: prop, comision, total, cobradoPor: v.cobrado_por_nombre || "",
    };
  };

  // Sprint 43.0 — Comprobante térmico de CORTESÍA (datos de solo lectura; nunca re-aplica ni mueve caja).
  const imprimirCortesia = (c, { reimpresion = false } = {}) => {
    if (!c) return;
    const cfg = loadPrinterConfig();
    const brand = sucursal?.nombre || "PARKNOVA";
    const payload = {
      brand, sucursal: sucursal?.nombre,
      operador: c.operador || user?.usuario_corto || user?.email || "",
      fecha: c.fecha ? fmtDateTime(c.fecha) : fmtDateTime(new Date()),
      folio: c.folio, motivo: c.motivo, importe: c.importe, reimpresion,
    };
    const doNative = () => { setRecibo(null); setCortesiaReceipt(payload); setTimeout(() => { try { window.scrollTo(0, 0); } catch { /* noop */ } window.print(); }, 150); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildCortesiaTicket({ ...payload, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Comprobante de cortesía enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  const cargarCortesiasTurno = async () => {
    if (!caja?.id_caja) return;
    setCortesiasLoading(true);
    try { const { data } = await api.get(`/operativa/cortesias?id_caja=${caja.id_caja}`); setCortesiasTurno(data || []); }
    catch { /* noop */ }
    finally { setCortesiasLoading(false); }
  };
  const abrirComprobantesCortesia = () => { setCortesiaComprobOpen(true); cargarCortesiasTurno(); };

  // Sprint 11.0 — Captura híbrida (IA): inyecta el JSON devuelto en el formulario (modo rescate: editable)
  const aiParse = async (payload) => {
    setAiLoading(true);
    try {
      const { data } = await aiParseApi(payload, { timeout: 90000 });
      setForm((f) => ({
        ...f,
        nombre_cliente: data.nombre_cliente || f.nombre_cliente,
        marca_modelo: data.marca_modelo || f.marca_modelo,
        color: data.color || f.color,
        placa: (data.placas || f.placa || "").toUpperCase(),
        objetos_valor: data.objetos_valor || f.objetos_valor,
      }));
      toast.success("Datos rellenados por IA. Revisa y ajusta si es necesario.");
      setMetodoRegistro(payload?.modo === "imagen" ? "FOTO_IA" : "VOZ_IA");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo analizar");
    } finally { setAiLoading(false); }
  };

  // Cámara + compresión en canvas (Sprint 34.1 Modo Flash: máx 800px, JPEG 0.6 → más liviano/rápido)
  const onFotoSeleccionada = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 800;
        let { width: w, height: h } = img;
        if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const b64 = canvas.toDataURL("image/jpeg", 0.6);
        aiParse({ modo: "imagen", imagen_base64: b64 });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Dictado por voz (Web Speech API, transcripción local — costo cero)
  // Sprint 34.1 (Modo Flash) — interimResults: muestra el texto en vivo mientras el valet habla.
  const dictar = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Tu navegador no soporta dictado por voz");
    if (escuchando) { dictWantStopRef.current = true; try { recognitionRef.current?.stop(); } catch {} return; }
    dictWantStopRef.current = false;
    dictCommittedRef.current = "";
    dictSessionRef.current = "";
    dictarBufferRef.current = "";
    setInterimTexto("");
    const SILENCE_MS = 2800; // tolerancia a silencio continuo (~2.5-3s) antes de finalizar
    const clearSilence = () => { if (dictSilenceRef.current) { clearTimeout(dictSilenceRef.current); dictSilenceRef.current = null; } };
    const startRec = () => {
      const rec = new SR();
      recognitionRef.current = rec;
      rec.lang = "es-MX";
      rec.interimResults = true;
      rec.continuous = true;
      rec.maxAlternatives = 1;
      rec.onstart = () => setEscuchando(true);
      rec.onerror = (e) => {
        if (e.error === "no-speech" || e.error === "aborted") return;
        dictWantStopRef.current = true; clearSilence();
        setEscuchando(false); toast.error("No se pudo capturar el audio");
      };
      rec.onresult = (ev) => {
        let texto = "";
        for (let i = 0; i < ev.results.length; i++) texto += ev.results[i][0].transcript;
        dictSessionRef.current = texto;
        const full = `${dictCommittedRef.current} ${texto}`.trim();
        dictarBufferRef.current = full;
        setInterimTexto(full);
        clearSilence();
        dictSilenceRef.current = setTimeout(() => { dictWantStopRef.current = true; try { rec.stop(); } catch {} }, SILENCE_MS);
      };
      rec.onend = () => {
        clearSilence();
        if (!dictWantStopRef.current) {
          // Pausa breve: el navegador cortó; conservar lo dicho y continuar escuchando.
          dictCommittedRef.current = `${dictCommittedRef.current} ${dictSessionRef.current}`.trim();
          dictSessionRef.current = "";
          try { startRec(); return; } catch {}
        }
        setEscuchando(false);
        const texto = (dictarBufferRef.current || "").trim();
        setInterimTexto("");
        if (texto) { toast(`Dictado: "${texto}"`); aiParse({ modo: "texto", texto }); }
      };
      rec.start();
    };
    setEscuchando(true); // indicador instantáneo (no espera a onstart)
    startRec();
  };

  // Entrega final desde el modal (estado SOLICITADO -> ENTREGADO)
  const entregar = async () => {
    const v = scanTarget;
    if (!v) return;
    // Candado financiero: bloquear entrega si no se ha cobrado
    if (cobraValet && !esPagado(v)) {
      toast.error("⚠️ Este vehículo debe ser cobrado antes de registrar su entrega.");
      return;
    }
    setCobrando(true);
    try {
      await avanzarHasta(v, "Entregado");
      toast.success("Vehículo entregado");
      setScanTarget(null);
      load(sucursal.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCobrando(false); }
  };

  // Sprint 8.0 — retroceder estado (protegido por modal de confirmación)
  const confirmarRegresar = async () => {
    const v = regresarTarget;
    if (!v) return;
    const idx = ESTADOS.indexOf(v.estado_valet);
    if (idx <= 0) { setRegresarTarget(null); return; }
    const prev = ESTADOS[idx - 1];
    setRegresando(true);
    try {
      await cambiarEstadoValet(v.id_detalle, { estado_valet: prev });
      toast.success(`Regresado a ${LABEL[prev]}`);
      setRegresarTarget(null);
      load(sucursal.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setRegresarTarget(null);
    } finally { setRegresando(false); }
  };

  // Cierre automático (5s) de la ventana de COBRO EXITOSO de Valet (no aplica a pagos en proceso).
  useAutoDismiss(!!pagoExitoso, () => setPagoExitoso(null));

  // Sprint 46.0 — puente estable hacia los handlers de tarjeta (siempre los últimos, sin closures obsoletos).
  const handlersRef = useRef({});
  const cardApi = useMemo(() => ({ get: () => handlersRef.current }), []);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#FFCC00]" /></div>;

  // Filtro global client-side (placa, llave, cliente, marca/modelo) — case-insensitive
  const q = searchQuery.trim().toLowerCase();
  const matches = (v) =>
    !q ||
    [v.placa, v.folio_impreso, v.nombre_cliente, v.marca_modelo]
      .some((f) => (f || "").toString().toLowerCase().includes(q));

  // Sprint 13.0 — Estatus de pago derivado de estado_ticket (autoritativo del backend; el valet NO lo altera).
  // Solo aplica si el servicio de valet tiene costo (> 0).
  const cobraValet = Number(tarifaValet) > 0;
  // Menú dinámico por modelo de cobro (UI pura). Se lee de la sucursal en localStorage; sin peticiones extra.
  const modeloSucursal = sucursal?.modelo_cobro || modeloCobro || "COMBINADO";
  const soloValet = modeloSucursal === "SOLO_VALET";
  const turnoConMovimientos = !!caja && items.length > 0;
  const salirOperativo = () => { logout(); navigate("/"); };
  const intentarSalir = () => { if (turnoConMovimientos) setLogoutWarnOpen(true); else salirOperativo(); };
  const esPagado = (v) => v.estado_ticket === "Pagado";
  const esPorPagar = (v) => cobraValet && v.estado_ticket === "Activo";
  const matchesPago = (v) => {
    if (!cobraValet || filtroPago === "todos") return true;
    if (filtroPago === "pagados") return esPagado(v);
    if (filtroPago === "porpagar") return esPorPagar(v);
    return true;
  };
  const visibleItems = items.filter((v) => matches(v) && matchesPago(v));
  const porPagarCount = cobraValet ? items.filter(esPorPagar).length : 0;
  const pagadosCount = cobraValet ? items.filter(esPagado).length : 0;

  // Sprint 17 — Desglose autoritativo para el cobro con tarjeta (Point). Mismo cálculo que Terminal.jsx.
  const scanActivoPt = scanTarget?.estado_ticket === "Activo";
  const estanciaPt = scanActivoPt ? Number(cobroInfo?.monto_estacionamiento || 0) : 0;
  const valetPt = scanActivoPt ? (cobroInfo ? Number(cobroInfo.monto_valet || 0) : (tarifaValet || 0)) : 0;
  const subtotalTarjeta = Math.round((estanciaPt + valetPt) * 100) / 100;
  const trasladaComision = !!comisionCfg?.trasladar_comision_tarjeta;
  const propinaNum = propinasActivas ? Math.max(0, Number(propina) || 0) : 0;
  const baseComisionable = Math.round((subtotalTarjeta + propinaNum) * 100) / 100;
  const cargoServicio = trasladaComision
    ? Math.round((baseComisionable * (Number(comisionCfg.tasa_comision_porcentaje) / 100) + Number(comisionCfg.comision_cargo_fijo)) * 100) / 100
    : 0;
  const granTotalTarjeta = Math.round((subtotalTarjeta + propinaNum + cargoServicio) * 100) / 100;
  // Sprint 17.2 — tope de seguridad configurable: la propina no puede exceder el tope % del subtotal
  const topePropinaMonto = Math.round(subtotalTarjeta * (Number(topePropinaPct) || 100) / 100 * 100) / 100;
  const propinaExcede = propinasActivas && propinaNum > topePropinaMonto + 0.0001;

  // Resalta la primera coincidencia del término de búsqueda dentro de un texto
  const highlight = (text) => {
    const s = text == null ? "" : String(text);
    if (!q || !s) return s;
    const idx = s.toLowerCase().indexOf(q);
    if (idx === -1) return s;
    return (
      <>
        {s.slice(0, idx)}
        <mark className="bg-[#FFCC00]/40 text-[#FFCC00] rounded-sm px-0.5">{s.slice(idx, idx + q.length)}</mark>
        {s.slice(idx + q.length)}
      </>
    );
  };

  handlersRef.current = { slaFor, permanenceFor, fmtEsperaFinal, seqDeFolio, highlight, esPagado, pagoDesdeCard, imprimirComprobantePago, reimprimir, setPhotoTarget, setNotaTarget, setNotaTexto, infoCobrado, openCobro, setRegresarTarget, avanzar };

  return (
    <div className={`min-h-screen bg-[#0A0A0A] text-white flex flex-col overflow-x-hidden ${sunMode ? "sun" : ""}`} data-testid="valet-root" data-sun-mode={sunMode ? "1" : "0"}>
      <ValetHeader
        sucursal={sucursal} user={user} caja={caja}
        sunMode={sunMode} toggleSunMode={toggleSunMode} soundOn={soundOn} toggleSound={toggleSound}
        setPrinterCfgOpen={setPrinterCfgOpen}
        cajaBarRef={cajaBarRef} setCajaState={setCajaState} comisionCfg={comisionCfg} setCaja={setCaja} load={load}
        setFondoTurno={setFondoTurno} setAbrirTurnoOpen={setAbrirTurnoOpen} modeloCobro={modeloCobro} tarifaValet={tarifaValet}
        soloValet={soloValet} navigate={navigate} intentarSalir={intentarSalir} cajaState={cajaState}
        abrirComprobantesCortesia={abrirComprobantesCortesia}
      />

      {/* Barra de Aforo — visible en el tablero Valet cuando hay turno abierto */}
      {caja && <AforoBar aforo={aforo} />}

      <div className="px-5 lg:px-8 py-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight">Valet Parking</h1>
          <div className="flex items-center gap-1.5" data-testid="valet-ws-status">
            <ConnectionStatus />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setHistoryOpen(true)} data-testid="valet-historial-btn" className="h-12 px-3 md:px-4 border border-white/20 hover:border-[#FFCC00] text-white/80 font-bold uppercase tracking-wider text-sm flex items-center gap-2"><History className="h-4 w-4" /> <span className="hidden md:inline">Historial</span></button>
          <button onClick={abrirRecepcion} data-testid="valet-recibir-btn" className="btn-hard h-12 px-4 md:px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus className="h-5 w-5" /> <span className="hidden md:inline">Recibir vehículo</span></button>
        </div>
      </div>

      {/* Barra de búsqueda (client-side) */}
      <div className="px-5 lg:px-8 pb-4">
        <div className="flex items-center gap-3 max-w-xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="valet-search-input" placeholder="Buscar por placa, llave, marca o cliente..."
              className="h-11 rounded-none bg-white/5 border-white/20 text-white placeholder:text-white/40 pl-10 pr-10 focus-visible:ring-2 focus-visible:ring-[#FFCC00]" />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} data-testid="valet-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {q && (
            <span data-testid="valet-search-count" className="shrink-0 text-xs font-bold uppercase tracking-wider text-[#FFCC00] whitespace-nowrap">
              {visibleItems.length} {visibleItems.length === 1 ? "coincidencia" : "coincidencias"}
            </span>
          )}
        </div>
        {/* Sprint 13.0 — Filtros por estatus de pago + contador de "Por Pagar" (solo si el valet tiene costo) */}
        {cobraValet && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="valet-pago-filtros">
            {[
              { v: "todos", label: "Todos", count: items.length },
              { v: "pagados", label: "Pagados", count: pagadosCount },
              { v: "porpagar", label: "Por Pagar", count: porPagarCount },
            ].map((f) => {
              const active = filtroPago === f.v;
              const accent = f.v === "porpagar" ? "#EF4444" : f.v === "pagados" ? "#10B981" : "#FFCC00";
              return (
                <button key={f.v} onClick={() => setFiltroPago(f.v)} data-testid={`valet-filtro-${f.v}`}
                  className="h-9 px-3 text-xs font-bold uppercase tracking-wider border flex items-center gap-2 transition-colors"
                  style={active ? { backgroundColor: accent, borderColor: accent, color: "#0A0A0A" } : (sunMode ? { borderColor: "#E7E1D3", color: "#4B5563" } : { borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" })}>
                  {f.label}
                  <span className="text-[10px] font-black px-1.5 py-0.5 rounded-sm" style={active ? { backgroundColor: "rgba(0,0,0,0.15)" } : (sunMode ? { backgroundColor: "rgba(0,0,0,0.06)" } : { backgroundColor: "rgba(255,255,255,0.1)" })}>{f.count}</span>
                </button>
              );
            })}
            {porPagarCount > 0 && (
              <span data-testid="valet-porpagar-alert" className="text-xs font-bold uppercase tracking-wider text-[#EF4444] flex items-center gap-1.5 ml-auto">
                <AlertTriangle className="h-4 w-4" /> {porPagarCount} {porPagarCount === 1 ? "coche por cobrar" : "coches por cobrar"}
              </span>
            )}
          </div>
        )}
        {/* Escáner QR / lector de código de barras */}
        <div className="flex items-center gap-2 max-w-xs mt-3">
          <div className="relative flex-1">
            <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#FFCC00]" />
            <Input value={scanValue} onChange={(e) => setScanValue(sanitizeFolio(e.target.value))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScan(scanValue); } }}
              data-testid="valet-scan-input" placeholder="Escanear QR (folio)…"
              className="h-11 rounded-none bg-[#FFCC00]/5 border-[#FFCC00]/40 text-white placeholder:text-white/40 pl-10 focus-visible:ring-2 focus-visible:ring-[#FFCC00]" />
          </div>
          <button type="button" onClick={() => setQrCamOpen(true)} data-testid="valet-qr-cam-btn" title="Escanear con la cámara"
            className="h-11 w-11 shrink-0 bg-[#FFCC00] text-[#0A0A0A] flex items-center justify-center hover:bg-[#e6b800] transition-colors">
            <Camera className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Sprint 12.0 — Escáner QR por cámara (móvil). Reutiliza onScan (mismo flujo que la pistola USB). */}
      <QrCamScanner open={qrCamOpen} onClose={() => setQrCamOpen(false)} onResult={(text) => { setQrCamOpen(false); onScan(text); }} />

      {/* Sprint 14.0 — Panel de periféricos / motor de impresión */}
      <ConfiguracionImpresoraModal open={printerCfgOpen} onClose={() => setPrinterCfgOpen(false)} />

      {/* Sprint 31.0 — Abrir Turno: captura directa de Fondo Inicial (disponible en todos los modelos) */}
      <AbrirTurnoModal abrirTurnoOpen={abrirTurnoOpen} setAbrirTurnoOpen={setAbrirTurnoOpen} fondoTurno={fondoTurno} setFondoTurno={setFondoTurno} abrirTurnoValet={abrirTurnoValet} openingTurno={openingTurno} />

      {/* Sprint 32.0 — Candado de salida: BLOQUEA el cierre de sesión si hay turno con movimientos (sin bypass) */}
      <LogoutBloqueadoModal logoutWarnOpen={logoutWarnOpen} setLogoutWarnOpen={setLogoutWarnOpen} />

      {/* Kanban */}
      <div className="flex-1 px-5 lg:px-8 pb-8">
        {q && visibleItems.length === 0 ? (
          <div data-testid="valet-search-empty" className="flex flex-col items-center justify-center py-20 text-center">
            <div className="h-14 w-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <Search className="h-6 w-6 text-white/40" />
            </div>
            <p className="text-white/70 font-bold">Sin resultados para tu búsqueda</p>
            <p className="text-white/40 text-sm mt-1">No hay vehículos que coincidan con "{searchQuery}".</p>
            <button onClick={() => setSearchQuery("")} data-testid="valet-search-empty-clear"
              className="mt-4 h-9 px-4 border border-white/20 hover:border-[#FFCC00] text-xs font-bold uppercase tracking-wider">Limpiar búsqueda</button>
          </div>
        ) : (
        <div className="flex lg:grid lg:grid-cols-4 gap-4 overflow-x-auto lg:overflow-visible snap-x snap-mandatory lg:snap-none -mx-5 px-5 lg:mx-0 lg:px-0 pb-2 lg:pb-0" data-testid="valet-kanban">
          {ESTADOS.map((estado) => {
            // Defensivo: un heredado 'Recibido' se bucketiza en la columna 'En_Resguardo'.
            let cards = visibleItems.filter((i) => colDeEstado(i.estado_valet) === estado);
            // Sprint 24.0 — "Solicitado": el vehículo con mayor espera siempre hasta arriba.
            if (estado === "Solicitado") {
              cards = [...cards].sort((a, b) =>
                new Date(a.solicitado_at || a.fecha_entrada) - new Date(b.solicitado_at || b.fecha_entrada)
              );
            }
            return (
              <div key={estado} className="bg-white/5 border border-white/10 flex flex-col shrink-0 w-[85vw] max-lg:landscape:w-[46vw] snap-center lg:w-auto lg:shrink" data-testid={`valet-col-${estado}`}>
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#FFCC00]">{LABEL[estado]}</span>
                  <span className="text-xs font-display font-bold bg-white/10 px-2 py-0.5">{cards.length}</span>
                </div>
                <div className="p-3 space-y-3 flex-1 min-h-[120px]">
                  {cards.map((v) => (
                    <ValetVehicleCard key={v.id_detalle} v={v} estado={estado} cobraValet={cobraValet} q={searchQuery} now={nowTick} api={cardApi} />
                  ))}
                  {cards.length === 0 && <p className="text-white/30 text-xs text-center py-6">—</p>}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Form recibir */}
      <RecibirVehiculoModal
        formOpen={formOpen} cerrarRecepcion={cerrarRecepcion} setFormOpen={setFormOpen}
        fotoIaActiva={fotoIaActiva} vozIaActiva={vozIaActiva} fileInputRef={fileInputRef}
        aiLoading={aiLoading} dictar={dictar} escuchando={escuchando} onFotoSeleccionada={onFotoSeleccionada}
        form={form} setForm={setForm} recibir={recibir} saving={saving}
      />

      {/* Sprint Pernoctas — Recepción de Inventario Nocturno al iniciar turno */}
      <RecepcionInventarioModal recepcionOpen={recepcionOpen} setRecepcionOpen={setRecepcionOpen} inventarioPatio={inventarioPatio} />

      {/* Sprint 8.0 — Modal preventivo: caja cerrada + Valet con costo */}
      <CajaWarningModal warnOpen={warnOpen} setWarnOpen={setWarnOpen} irAbrirCaja={irAbrirCaja} />

      {/* Sprint 8.0 — Modal de confirmación para retroceder estado */}
      <RegresarModal regresarTarget={regresarTarget} setRegresarTarget={setRegresarTarget} confirmarRegresar={confirmarRegresar} regresando={regresando} />

      {/* Sprint 9.0 (Fase 3) — Modal de confirmación de recepción con secuencial gigante */}
      <ReciboSuccessModal successOpen={successOpen} setSuccessOpen={setSuccessOpen} recibo={recibo} setRecibo={setRecibo} setPhotoDefaultTipo={setPhotoDefaultTipo} setPhotoTarget={setPhotoTarget} imprimirTicketValet={imprimirTicketValet} />

      {/* Sprint 25.0 — Comprobante de Pago (recibo de dinero) tras un cobro exitoso */}
      <PagoExitosoModal pagoExitoso={pagoExitoso} setPagoExitoso={setPagoExitoso} imprimirComprobantePago={imprimirComprobantePago} />

      {/* Sprint 26.0 — Selección de motivo de cortesía */}
      <CortesiaModal cortesiaOpen={cortesiaOpen} setCortesiaOpen={setCortesiaOpen} cortesiaMotivo={cortesiaMotivo} setCortesiaMotivo={setCortesiaMotivo} motivosCortesia={motivosCortesia} aplicarCortesia={aplicarCortesia} cobrando={cobrando} />

      {/* Sprint 43.0 — Comprobantes de cortesía del turno (reimpresión) + recibo térmico oculto */}
      <CortesiaComprobantesDialog
        open={cortesiaComprobOpen} onClose={() => setCortesiaComprobOpen(false)}
        cortesias={cortesiasTurno} loading={cortesiasLoading}
        onReimprimir={(c) => imprimirCortesia(c, { reimpresion: true })} />
      <CortesiaThermalReceipt cortesia={cortesiaReceipt} paperWidth={80} />

      {/* Sprint 26.0 — Nota de incidencia (teclado) */}
      <NotaIncidenciaModal notaTarget={notaTarget} setNotaTarget={setNotaTarget} notaTexto={notaTexto} setNotaTexto={setNotaTexto} guardarNota={guardarNota} notaSaving={notaSaving} />

      <GestionVehiculoModal
        scanTarget={scanTarget} setScanTarget={setScanTarget} pointOpen={pointOpen} cobraValet={cobraValet}
        cobroInfo={cobroInfo} tarifaValet={tarifaValet} bpMode={bpMode} setBpMode={setBpMode}
        bpEvidencias={bpEvidencias} setBpEvidencias={setBpEvidencias} setCobroInfo={setCobroInfo} setAutoEntregar={setAutoEntregar}
        costoBoletoPerdido={costoBoletoPerdido} entregar={entregar} cobrando={cobrando}
        metodoPago={metodoPago} setMetodoPago={setMetodoPago} aceptaTarjeta={aceptaTarjeta} autoEntregar={autoEntregar}
        cobrarYSolicitar={cobrarYSolicitar} cortesiasActivas={cortesiasActivas} puedeCortesia={puedeCortesia}
        setCortesiaMotivo={setCortesiaMotivo} setCortesiaOpen={setCortesiaOpen}
        capturarEvidenciaBP={capturarEvidenciaBP} bpUploading={bpUploading}
      />

      {/* Sprint 17 — Cobro con terminal Mercado Pago Point (idéntico a Terminal.jsx) */}
      <PointModal
        pointOpen={pointOpen} cerrarPagoPoint={cerrarPagoPoint} scanTarget={scanTarget} subtotalTarjeta={subtotalTarjeta}
        pointStatus={pointStatus} propinasActivas={propinasActivas} topePropinaMonto={topePropinaMonto}
        propina={propina} setPropina={setPropina} propinaCustom={propinaCustom} setPropinaCustom={setPropinaCustom}
        propinaExcede={propinaExcede} topePropinaPct={topePropinaPct} propinaNum={propinaNum}
        trasladaComision={trasladaComision} cargoServicio={cargoServicio} granTotalTarjeta={granTotalTarjeta}
        pointDevicesLoading={pointDevicesLoading} pointDevices={pointDevices} cargarDevices={cargarDevices}
        pointDevice={pointDevice} elegirDevice={elegirDevice} enviarCobroPoint={enviarCobroPoint}
        pointMsg={pointMsg} pointOrderStatus={pointOrderStatus} pointTech={pointTech}
        consultarManual={consultarManual} cancelarPendiente={cancelarPendiente}
        setPointStatus={setPointStatus} setPointTxId={setPointTxId} setPointTech={setPointTech}
        setPointOrderStatus={setPointOrderStatus} setPointMsg={setPointMsg}
      />

      {/* Sprint 9.0 (Fase 5+) — Historial de Entregas del día */}
      <HistorialModal historyOpen={historyOpen} setHistoryOpen={setHistoryOpen} items={items} />

      {/* Plantilla de impresión térmica a dos vías (oculta en web, visible en @media print) */}
      {recibo && <ValetTickets recibo={recibo} sucursal={sucursal} leyenda={leyendaTicket} horario={horarioAtencion} tarifaTexto={Number(tarifaValet) > 0 ? `$${Number(tarifaValet).toFixed(2)} Tarifa Fija` : null} />}

      {photoTarget && <PhotoDialog valet={photoTarget} defaultTipo={photoDefaultTipo} onChanged={() => sucursal && load(sucursal.id)} onClose={() => { setPhotoTarget(null); setPhotoDefaultTipo("Frente"); if (sucursal) load(sucursal.id); }} />}
    </div>
  );
}
